import { WatchQueryFetchPolicy } from '@apollo/client';
import { DocumentNode } from 'graphql';

import type { Country } from '@skippay/core/@types/country';
import { GqlVariables } from '@skippay/core/utils/queryUtils';
import { Maybe } from '@skippay/core/utils/types';

import { CustomerContext, CustomerContextDomain } from '../types';

export type SourceData = Record<string, never>;

type MutationInputValue = Maybe<
	string | number | boolean | MutationInputValue[] | { [key: string]: MutationInputValue }
>;

export type MutationVariables = { [key: string]: MutationInputValue };

export interface ContextEnv {
	country: Country;
}

export interface DomainSource<TData> {
	kind?: 'query';
	domains: readonly CustomerContextDomain[];
	query: DocumentNode;
	variables?: GqlVariables;
	getVariables?: () => GqlVariables;
	fetchPolicy?: WatchQueryFetchPolicy;
	toModel(data: TData, ctx: Readonly<CustomerContext>): Partial<CustomerContext>;
}

export interface DerivedSource {
	kind: 'derived';
	domains: readonly CustomerContextDomain[];
	toModel(ctx: Readonly<CustomerContext>): Partial<CustomerContext>;
}

export interface MutationSource<TData, TRequest extends MutationVariables> {
	kind: 'mutation';
	domains: readonly CustomerContextDomain[];
	mutation: DocumentNode;
	prepare(ctx: Readonly<CustomerContext>, env: ContextEnv): TRequest | undefined;
	getVariables(request: TRequest): MutationVariables;
	toModel(data: TData, request: TRequest): Partial<CustomerContext>;
}

export type AnyMutationSource = MutationSource<SourceData, MutationVariables>;

export type ContextSource = DomainSource<SourceData> | DerivedSource | AnyMutationSource;

export type ContextAdapter = readonly ContextSource[];

export const isDerivedSource = (source: ContextSource): source is DerivedSource =>
	source.kind === 'derived';

export const isMutationSource = (source: ContextSource): source is AnyMutationSource =>
	source.kind === 'mutation';
